import React, { ReactNode } from 'react'
import { isApolloError } from '@apollo/client/errors'
import hoistNonReactStatics from 'hoist-non-react-statics'
import { Subtract } from 'utility-types'
import {
  ToastAnchor,
  useToasts,
  ToastProps,
} from 'shared/timber/hydrogen/toast'

import Snackbar from '~/views/components/ui/Snackbar'
import * as TypeGuards from 'shared/utils/type-guards'

/**
 * updated for sc-16399 to implement useToast from hydrogen
 * by @creednaylor and @brennongs
 *
 * reverted 10/8/21 because of blocking changes by @brennongs
 *
 */

type Severity = ToastProps['severity']
type Message = string | React.ReactNode

// this needs to stay for backwards compatibility
const VARIANT: Record<Severity, Severity> = {
  error: 'error',
  info: 'info',
  warning: 'warning',
  success: 'success',
}

export interface FlashMessage {
  title?: string
  message?: Message
  variant?: Severity
  autoHideDuration?: number
  error?: Error | unknown
}

export type ShowFlashMessage = (message: FlashMessage) => void

interface FlashMessageContextProviderProps {
  showFlashMessage: ShowFlashMessage
  toastNotificationsAreEnabledState?: boolean

  enableToastNotifications?(isEnabled: boolean): void
}

class DecoratedFlashMessage {
  title?: string
  message: FlashMessage['message']
  severity: 'info' | 'error' | 'warning' | 'success'

  constructor(flashMessage: FlashMessage) {
    this.message = this.getMessage(flashMessage)
    this.severity = this.getVariant(flashMessage)

    if (flashMessage.title) {
      this.title = flashMessage.title
    }
  }

  getMessage(flashMessage: FlashMessage): FlashMessage['message'] {
    return (
      flashMessage.message ||
      (flashMessage.error instanceof Error && flashMessage.error.message) ||
      (typeof flashMessage.error === 'string' && flashMessage.error) ||
      ''
    )
  }

  getVariant(
    flashMessage: FlashMessage,
  ): 'info' | 'error' | 'warning' | 'success' {
    if (flashMessage.error) return 'error'
    if (flashMessage.variant) return flashMessage.variant

    return 'info'
  }
}

const FlashMessageContext =
  React.createContext<FlashMessageContextProviderProps | null>(null)
// test redefines this function
// eslint-disable-next-line
let useFlashMessageContext = () => {
  const flashMessageContext = React.useContext(FlashMessageContext)
  const toasts = useToasts()

  if (flashMessageContext?.toastNotificationsAreEnabledState) {
    if (toasts === null)
      throw new Error('useToasts was used outside of ToastAnchor')

    return {
      showFlashMessage: (newFlashMessage: FlashMessage) => {
        toasts.open(new DecoratedFlashMessage(newFlashMessage))
      },
    }
  } else {
    if (flashMessageContext === null)
      throw new Error('FlashMessageContext was used outside of the provider')

    return flashMessageContext
  }
}

const useFlashMessage = () => {
  const mounted = React.useRef(true)
  const [messageQueue, setMessageQueue] = React.useState<FlashMessage[]>([])

  /**
   * Updating state for components that have been unmounted is a no-op and leads
   * to fun-to-debug memory leaks. Ideally the components that are calling this hook
   * are handling that logic themselves. This check is a last defense against that.
   *
   * By tracking this ref we can safely `setMessageQueue` for mounted components and
   * ignore it for unmounted components.
   */
  React.useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  })

  const showFlashMessage = (newFlashMessage: FlashMessage) => {
    if (mounted.current) {
      setMessageQueue((prevMessageQueue) => [
        ...prevMessageQueue,
        newFlashMessage,
      ])
    }
  }

  const dismissFlashMessage = () => {
    if (mounted.current) {
      setMessageQueue(messageQueue.splice(1))
    }
  }

  return {
    showFlashMessage,
    dismissFlashMessage,
    currentMessage: messageQueue[0],
  }
}

interface FlashMessageProviderProps {
  children: ReactNode
}

const FlashMessageProvider: React.FC<FlashMessageProviderProps> = ({
  children,
}) => {
  const [
    toastNotificationsAreEnabledState,
    setToastNotificationsAreEnabledState,
  ] = React.useState<boolean>(false)

  const getError = (error: Error | unknown | undefined) => {
    if (!TypeGuards.isDefined<Error>(error)) return null

    if (isApolloError(error) && error.graphQLErrors.length > 0) {
      return error.graphQLErrors[0]
    }

    /**
     * Our API will return GraphQLExecution errors that cause a 400 status
     * code. This is a Network-level error, but the response body has more
     * helpful error messages that are intended for the end user. Without
     * doing this, then the user will only be getting super helpful messages like:
     *
     * `Response not successful: Received status code 400`
     */
    if (
      isApolloError(error) &&
      TypeGuards.isServerError(error.networkError) &&
      // @ts-ignore - todo: ENG-2946 -- Adding some specificity to this in case to ensure we're not causing more errors
      error.networkError.result?.errors?.[0]
    ) {
      // @ts-ignore - todo: ENG-2946 -- Adding some specificity to this in case to ensure we're not causing more errors
      return error.networkError.result.errors[0]
    }

    return error
  }

  const { showFlashMessage, dismissFlashMessage, currentMessage } =
    useFlashMessage()

  const {
    autoHideDuration = 3000,
    error: flashError,
    message: flashMessage,
    variant,
  } = currentMessage || {}

  const error = getError(flashError)
  const message = error ? error.message : flashMessage

  const toasts = useToasts()

  if (toastNotificationsAreEnabledState) {
    const showFlashMessage = (newFlashMessage: FlashMessage) => {
      const error = getError(newFlashMessage)

      if (error) {
        toasts.open(new DecoratedFlashMessage(error))

        return null
      }

      toasts.open(new DecoratedFlashMessage(newFlashMessage))

      return null
    }

    return (
      <FlashMessageContext.Provider
        value={{
          showFlashMessage,
          toastNotificationsAreEnabledState,
        }}
      >
        <ToastAnchor>{children}</ToastAnchor>
      </FlashMessageContext.Provider>
    )
  } else {
    return (
      <FlashMessageContext.Provider
        value={{
          showFlashMessage,
          toastNotificationsAreEnabledState,
          enableToastNotifications: (isEnabled) => {
            if (isEnabled && !toastNotificationsAreEnabledState) {
              setToastNotificationsAreEnabledState(isEnabled)
            }
          },
        }}
      >
        <Snackbar
          open={Boolean(currentMessage)}
          autoHideDuration={autoHideDuration}
          onClose={() => {
            dismissFlashMessage()
          }}
          message={message}
          variant={error ? 'error' : variant || 'error'}
        />
        {children}
      </FlashMessageContext.Provider>
    )
  }
}

export interface WithFlashMessage {
  showFlashMessage: ShowFlashMessage
}

const withFlashMessages = <P extends WithFlashMessage>(
  Component: React.ComponentType<P>,
) => {
  const FlashedComponent = React.forwardRef<any, Subtract<P, WithFlashMessage>>(
    (props, ref) => {
      const { showFlashMessage } = useFlashMessageContext()

      return (
        <Component
          {...(props as any)}
          ref={ref}
          showFlashMessage={showFlashMessage}
        />
      )
    },
  )

  return hoistNonReactStatics(FlashedComponent, Component)
}

export {
  FlashMessageProvider,
  useFlashMessageContext,
  withFlashMessages,
  VARIANT,
}
